import { useMutation, useQuery } from '@apollo/client/react'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import useOnScreen from '@src/hooks/useOnScreen'
import { gql } from '@src/lib/gql'
import React, { Fragment, ReactNode, useEffect, useRef, useState } from 'react'

export type ViewableTrackerProps = {
  viewableId: string
  viewableType: string
  viewableContext?: string

  parentId?: string
  parentType?: string
  parentContext?: string

  startedAt?: Date
  completedAt?: Date
  completionAsPercent?: number
  playbackPositionInSeconds?: number

  children?: ReactNode | ReactNode[]
  trackScrolling?: boolean
}

export const ViewableTracker = (props: ViewableTrackerProps) => {
  const trackerStartRef = useRef<HTMLDivElement>(null)
  const trackerStartIsVisible = useOnScreen(trackerStartRef)
  const trackerEndRef = useRef<HTMLDivElement>(null)
  const trackerEndIsVisible = useOnScreen(trackerEndRef)

  const { currentUser } = useCurrentUser()
  const [scrollPercent, setScrollPercent] = useState(0)
  const [isMounted, setIsMounted] = useState(false)

  const query = useQuery(
    gql`
      query (
        $parentContext: String
        $parentId: String
        $parentType: String
        $viewableContext: String
        $viewableId: String
        $viewableType: String
      ) {
        scoView(
          parentContext: $parentContext
          parentId: $parentId
          parentType: $parentType
          viewableContext: $viewableContext
          viewableId: $viewableId
          viewableType: $viewableType
        ) {
          id
          completedAt
          completionAsPercent
          parentContext
          parentId
          parentType
          playbackPositionInSeconds
          startedAt
          viewableContext
          viewableId
          viewableType
        }
      }
    `,
    {
      ssr: false,
      skip: !currentUser?.isRegistered,
      variables: {
        parentContext: props.parentContext,
        parentId: props.parentId,
        parentType: props.parentType,
        viewableContext: props.viewableContext,
        viewableId: props.viewableId,
        viewableType: props.viewableType,
      },
    }
  )
  const [upsertView, upsertViewMutation] = useMutation(gql`
    mutation UpsertView($input: UpsertViewInput!) {
      upsertView(input: $input) {
        view {
          id
          completedAt
          completionAsPercent
          playbackPositionInSeconds
          startedAt
          parentContext
          parentId
          parentType
          viewableContext
          viewableId
          viewableType
          contentTrackEnrollment {
            id
            progress {
              id
              startedAt
              completedAt
              completionAsPercent
              sectionStatuses {
                id
                startedAt
                completedAt
                completionAsPercent
                stepStatuses {
                  id
                  startedAt
                  completedAt
                  completionAsPercent
                  taskStatuses {
                    id
                    startedAt
                    completedAt
                    completionAsPercent
                  }
                }
              }
            }
          }
        }
      }
    }
  `)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (isMounted && currentUser?.isRegistered && props.viewableType && props.viewableId) {
      if (!upsertViewMutation.loading && query.data?.scoView) {
        const existingView = query.data.scoView
        const newCompletionAsPercent = props.completionAsPercent || scrollPercent
        const newCompletedAt = newCompletionAsPercent >= 95 ? new Date() : null
        const newStartedAt = props.startedAt || new Date()
        const newPlaybackPositionInSeconds = Math.round(props.playbackPositionInSeconds)

        upsertView({
          variables: {
            input: {
              completedAt: existingView.completedAt || newCompletedAt,
              completionAsPercent: Math.round(
                newCompletionAsPercent > existingView.completionAsPercent
                  ? newCompletionAsPercent
                  : existingView.completionAsPercent || 0
              ),
              playbackPositionInSeconds:
                newPlaybackPositionInSeconds > existingView.playbackPositionInSeconds
                  ? newPlaybackPositionInSeconds
                  : existingView.playbackPositionInSeconds || 0,
              startedAt: existingView.startedAt || newStartedAt,
              parentContext: props.parentContext,
              parentId: props.parentId,
              parentType: props.parentType,
              viewableId: props.viewableId,
              viewableType: props.viewableType,
              viewableContext: props.viewableContext,
            },
          },
        })
      }
    }
  }, [
    query.data?.scoView?.id,
    isMounted,
    scrollPercent,
    props.parentContext,
    props.parentType,
    props.parentId,
    props.viewableContext,
    props.viewableType,
    props.viewableId,
    props.startedAt,
    props.completedAt,
    props.completionAsPercent,
    props.playbackPositionInSeconds,
  ])

  useEffect(() => {
    if (props.trackScrolling) {
      if (trackerEndIsVisible) {
        setScrollPercent(100)
      } else {
        setScrollPercent(0)
      }
    }
  }, [trackerStartIsVisible, trackerEndIsVisible])

  return (
    <>
      {props.trackScrolling && <div ref={trackerStartRef} />}
      {props.children}
      {props.trackScrolling && <div ref={trackerEndRef} />}
    </>
  )
}
